import React, { useMemo, useState, useCallback, useEffect } from 'react';
import fileDownload from 'js-file-download';
import { useNavigate } from 'react-router-dom';

import {
	TranslatedString,
	useAjax,
	XhrStatus,
	XhrResponseType,
	Form,
	SearchField,
	Card,
	PrimaryButton,
	LocallySortedAndPaginatedTable,
	makeLocallySortedAndPaginatedTableColumnHeader,
	makeColumnHeader,
	Dialog
} from '@bosch/bt-ui';

import './PanelManagementPage.css';
import withErrorHandler from '../../components/ErrorHandler';
import RestfulCallLoading from '../../components/RestfulCallLoading';
import RestfulCallout from '../../components/RestfulCallout';
import PanelRadioOptions from './LocateComponent/PanelRadioOptions';
import FlaggedCheckBox from './LocateComponent/FlaggedCheckBox';
import { prepareLogData } from '../../components/Util';
import ServiceStateIndicator from '../../components/ServiceStateIndicator';

const TABLE_HEADERS = [
	makeLocallySortedAndPaginatedTableColumnHeader('CloudId', 'Cloud ID'),
	makeLocallySortedAndPaginatedTableColumnHeader('CompanyName', 'Company Name'),
	makeLocallySortedAndPaginatedTableColumnHeader('CompanySapNumber', 'SAP Company ID'),
	makeLocallySortedAndPaginatedTableColumnHeader('RemoteId', 'Remote Portal ID'),
	makeLocallySortedAndPaginatedTableColumnHeader('Flagged', 'Disable Management in Remote Portal')
];

const CLOUD_ID_TABLE_HEADERS = [
	makeLocallySortedAndPaginatedTableColumnHeader('CloudIdTable-CloudId', 'Cloud ID'),
	makeLocallySortedAndPaginatedTableColumnHeader('CloudIdTable-CompanyName', 'Company Name'),
	makeLocallySortedAndPaginatedTableColumnHeader('CloudIdTable-CompanySapNumber', 'SAP Company ID'),
	makeLocallySortedAndPaginatedTableColumnHeader('CloudIdTable-RemoteId', 'Remote Portal ID'),
	makeColumnHeader('CloudIdTable-Flagged', 'Disable Management in Remote Portal'),
	makeColumnHeader('CloudIdTable-RemoteConnectCloud', 'RemoteConnect-Cloud'),
	makeColumnHeader('CloudIdTable-RemoteConnectRSCMobileApp', 'RemoteConnectRSC-MobileApp'),
	makeColumnHeader('CloudIdTable-RemoteAlertPush', 'RemoteAlert-Push'),
	makeColumnHeader('CloudIdTable-DetailsButton', '')
];

function makeRow(token, isLoggedIn, locateInfo, onOpenModal, queryType) {
	const {
		companyName,
		companySapNumber,
		remoteId,
		cloudId,
		flagged,
		panelInfo,
	} = locateInfo;

	const remoteConnectActivated = panelInfo?.serviceStates?.find(item => item.serviceName === 'REMOTE_CONNECT')?.activated;
	const remoteConnectRscActivated = panelInfo?.serviceStates?.find(item => item.serviceName === 'REMOTE_CONNECT_RSC')?.activated;
	const remoteAlertActivated = panelInfo?.serviceStates?.find(item => item.serviceName === 'REMOTE_ALERT')?.activated;

	return queryType == 'cloudId' ? {
		key: cloudId,
		columns: [
			{
				key: 'CloudIdTable-CloudId',
				columnContent: cloudId
			},
			{
				key: 'CloudIdTable-CompanyName',
				columnContent: companyName
			},
			{
				key: 'CloudIdTable-CompanySapNumber',
				columnContent: companySapNumber
			},
			{
				key: 'CloudIdTable-RemoteId',
				columnContent: remoteId
			},
			{
				key: 'CloudIdTable-Flagged',
				columnContent:
					<FlaggedCheckBox
						token={token} 
						isLoggedIn={isLoggedIn} 
						flaggedInfo={locateInfo}
					/>
			},
			{
				key: 'CloudIdTable-RemoteConnectCloud',
				columnContent: 
					<ServiceStateIndicator isChecked={remoteConnectActivated}/>		
			},
			{
				key: 'CloudIdTable-RemoteConnectRSCMobileApp',
				columnContent: 
					<ServiceStateIndicator isChecked={remoteConnectRscActivated}/>	
			},
			{
				key: 'CloudIdTable-RemoteAlertPush',
				columnContent:
					<ServiceStateIndicator isChecked={remoteAlertActivated}/>	
			},
			{
				key: 'CloudIdTable-DetailsButton',
				columnContent:
					<PrimaryButton onClick={()=>onOpenModal(locateInfo)}><TranslatedString stringId="details"/></PrimaryButton>
			},
		]
	}
	:
	{
		key: cloudId,
		columns: [
			{
				key: 'CloudId',
				columnContent: cloudId
			},
			{
				key: 'CompanyName',
				columnContent: companyName
			},
			{
				key: 'CompanySapNumber',
				columnContent: companySapNumber
			},
			{
				key: 'RemoteId',
				columnContent: remoteId
			},
			{
				key: 'Flagged',
				columnContent: 
					<FlaggedCheckBox
						token={token} 
						isLoggedIn={isLoggedIn} 
						flaggedInfo={locateInfo}
					/>
			}
		]
	};
}

const PanelManagementPage = (props) => {
	const {
		token,
		isLoggedIn
	} = props;
	const [searchCloudId, setSearchText] = useState('');
	const [searchErrorMessage, setSearchErrorMessage] = useState();
	const [checkState, setCheckState] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [queryType, setQueryType] = React.useState('cloudId');
	const [textBoxPlaceholder, setTextBoxPlaceholder] = React.useState('Using comma to separate each Id');
	const [disableDownload, setDownloadDisable] = useState(true); 
	const [result, status, getPanelLocate, response] = useAjax(XhrStatus.Initial);
	const [exportResult, exportStatus, exportPanelLocate, exportResponse] = useAjax(XhrStatus.Initial, XhrResponseType.Binary);
	const [locateInfo, setLocateInfo] = useState('');
	const [openDialog, setOpenDialog] = useState(false);
	const [showCloudIdSearchHeader, setShowCloudIdSearchHeader] = useState(true);
	const navigate = useNavigate();

	const onOpenModal = (content) => {
		setOpenDialog(true);
		setLocateInfo(content);
	};

	const onCloseModal = () => {
		setOpenDialog(false);
		setLocateInfo('');
	};

	useEffect(
		() => {
			if (exportStatus !== XhrStatus.Finished)
				return;	

			fileDownload(exportResult, 'PanelLocate.csv');
		},
		[exportStatus, exportResult]
	);

	const onFormSubmit = useCallback(
		() => {
			const data = prepareLogData(token,'Panel_Management', 'Panel Management->Get locate button: ' + searchCloudId);
			// If useAjax(XhrStatus.InProgress) and not login yet, it will show 401
			// So we need to make sure token is avaliable to continue...
			if (token?.token) {
				if(isLoggedIn()) {
					setCheckState(true);
					if (searchCloudId)
					{
						if (searchErrorMessage == null)
						{
							setCurrentPage(1);
							getPanelLocate('GET', SUPPORTCONSOLE_API_URL + '/api/panelmanagement/v1/locate/' + encodeURIComponent(searchCloudId) + '?queryType=' + encodeURIComponent(queryType), null, undefined, { 'Authorization': 'Bearer ' + token.token });
							//Add activity log
							fetch(SUPPORTCONSOLE_API_URL + '/api/activity/v1/activitylog', {
								method: 'POST',
								headers: {
								'Content-Type': 'application/json',
								'Authorization': 'Bearer ' + token.token
								},
								body: JSON.stringify(data)
							})	
							if (queryType == 'cloudId')
							{
								setShowCloudIdSearchHeader(true)
							} else {
								setShowCloudIdSearchHeader(false);
							}
						}
					}
				}
				else {
					navigate('/');
				}
			}
		},
		[searchCloudId, queryType, searchErrorMessage, token.token, getPanelLocate, isLoggedIn, navigate]
	);

	const onExportClick = useCallback(
		() => {
			// If useAjax(XhrStatus.InProgress) and not login yet, it will show 401
			// So we need to make sure token is avaliable to continue...
			if (token?.token) {
				if(isLoggedIn()) {
					setCheckState(true);
					if (searchCloudId)
					{
						if (searchErrorMessage == null)
						{
							exportPanelLocate('GET', SUPPORTCONSOLE_API_URL + '/api/panelmanagement/v1/export/' + encodeURIComponent(searchCloudId) + '?queryType=' + encodeURIComponent(queryType), null, undefined, { 'Authorization': 'Bearer ' + token.token });
						}
					}
				}
				else {
					navigate('/');
				}
			}
		},
		[searchCloudId, queryType, searchErrorMessage, token.token, exportPanelLocate, isLoggedIn, navigate]
	);

	const rows = useMemo(
		() => {
			const allRows = (result?.data) ? result.data.map(item => makeRow(token, isLoggedIn, item, onOpenModal, queryType)) : [];
			if (allRows.length > 0)
			{
				setDownloadDisable(false);
			}

			return allRows;
		},
		[token, isLoggedIn, result]
	);

	useEffect(
		() => {
			if (queryType === 'cloudId') {
				setTextBoxPlaceholder('Using comma to separate each Id');
			}
			else if (queryType === 'sapNumber') {
				setTextBoxPlaceholder('SapNumber');
			}
			else if (queryType === 'remoteId') {
				setTextBoxPlaceholder('Remote Id');
			}

			setSearchText('');
			setDownloadDisable(true);
		},
		[queryType, textBoxPlaceholder]
	);

	return (
		<Card
			background="secondary"
			htmlAttributes={{
				id: 'panel-management-page',
				className: 'sim-search-input'
			}}>
			<RestfulCallout status={status} response={response} returnObj={result} />
			
			<div className="form-input">
				<div className='panel-radio-options'>
					<PanelRadioOptions
						label="Cloud ID"
						value={queryType === 'cloudId'}
						onChange={() => {setQueryType('cloudId')}}
						/>
					<PanelRadioOptions
							label="SAP Company ID"
							value={queryType === 'sapNumber'}
							onChange={() => {setQueryType('sapNumber')}}
						/>
					<PanelRadioOptions
							label="Remote Portal ID"
							value={queryType === 'remoteId'}
							onChange={() => {setQueryType('remoteId')}}
						/>
						(<TranslatedString stringId="panel-management-cbs-notes"/>)
				</div>
				<Form
					formAttributes={{
						onSubmit: onFormSubmit,
						method: 'get',
						action: ''
					}}
				>
					<SearchField
						fieldName="search"
						shouldAlwaysShowErrors={checkState}
						validationError={searchErrorMessage}
						onValidationError={(update) => {
							setSearchErrorMessage(update.value);

						}}
						htmlAttributes={{
							onChange: update => setSearchText(update.value),
							value: searchCloudId,
							placeholder: textBoxPlaceholder,
							required: true,
							
							pattern: '[0-9_a-z_A-Z,]+',
							title: 'Alphanumeric only'
						}}
					/>
					<PrimaryButton type="submit">Get Locate</PrimaryButton>
				</Form>
			</div>
			{
				<LocallySortedAndPaginatedTable
					headers={showCloudIdSearchHeader ? CLOUD_ID_TABLE_HEADERS : TABLE_HEADERS}
					rows={rows}
					numRowsPerPage={50}
					currentPage={currentPage}
					goToPageFunc={setCurrentPage}
					htmlAttributes={{
						className: 'user-management-table'
					}}
					errorMessage="Error occured"
					noResultsMessage={<TranslatedString stringId="No items found" />}
				/>
			}
			<Dialog onClose={onCloseModal}
				dialogOptions={{
					open: openDialog
				}}
				focusTrapped={false}
				title={<TranslatedString stringId="dialog-info"/>}
				alertType="info"
			>
				<div className="dialog-description">
					<pre>
						<code>
							{JSON.stringify(locateInfo, null, 2)}
						</code>
					</pre>
				</div>
			</Dialog>
			<RestfulCallLoading status={status} />
			<div className='export-csv'>
				<RestfulCallout status={exportStatus} response={exportResponse}/>
				<PrimaryButton type="submit" onClick={onExportClick} disabled={disableDownload}>Download</PrimaryButton>
			</div>
			<RestfulCallLoading status={exportStatus} />
		</Card>
	);
};

export default withErrorHandler(React.memo(PanelManagementPage));